import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useBrand } from "@/lib/brand";
import { useAuth } from "@/lib/auth";
import { Menu, X } from "lucide-react";
import { useState } from "react";

export default function Navbar() {
  const { brand, setBrand } = useBrand();
  const { user, logout } = useAuth();
  const loc = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const isAcademy = loc.pathname.startsWith("/academy");
  const isMedia = loc.pathname.startsWith("/studio") || loc.pathname.startsWith("/media");

  const accent = brand === "ai" ? "text-[#00FFC2]" : "text-[#A855F7]";
  const dotColor = brand === "ai" ? "bg-[#00FFC2]" : "bg-[#A855F7]";

  const linkBase = "text-sm tracking-wide text-gray-300 hover:text-white transition-colors";
  const active = "text-white";

  const items = brand === "ai"
    ? [
        { to: "/academy", label: "Academy" },
        { to: "/academy/courses", label: "Modules" },
        { to: "/academy/prompts", label: "Prompt Lab" },
        { to: "/blog", label: "Journal" },
      ]
    : [
        { to: "/studio", label: "Studio" },
        { to: "/studio#services", label: "Services" },
        { to: "/studio#work", label: "Work" },
        { to: "/studio#book", label: "Book a Call" },
      ];

  return (
    <header
      data-testid="site-nav"
      className="sticky top-0 z-50 glass border-b hairline"
      style={{ borderBottomColor: "var(--hairline)" }}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5 group">
          <span className={`inline-block w-2.5 h-2.5 rounded-full ${dotColor} shadow-[0_0_18px_currentColor]`} />
          {isMedia ? (
            <span className="font-display-media italic text-lg font-bold tracking-tight">
              Edit Bae <span className="text-[#A855F7] not-italic text-xs tracking-[0.28em] uppercase font-semibold">Studios</span>
            </span>
          ) : (
            <span className="font-display-ai text-base font-bold tracking-tight">
              AI CREATOR <span className={accent}>OS</span>
            </span>
          )}
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              data-testid={`nav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `${linkBase} ${isActive && (isAcademy || isMedia) ? active : ""}`
              }
            >
              {it.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <button
            data-testid="nav-brand-switcher"
            onClick={() => (brand === "ai" ? navigate("/studio") : navigate("/academy"))}
            className="text-xs tracking-[0.25em] uppercase px-3 py-1.5 border hairline rounded-full hover:bg-white/5 transition"
            title="Switch worlds"
          >
            {brand === "ai" ? "→ Studio" : "→ Academy"}
          </button>
          {user ? (
            <>
              <Link to="/dashboard" data-testid="nav-dashboard" className={linkBase}>Dashboard</Link>
              <button
                data-testid="nav-logout"
                onClick={logout}
                className="text-sm text-gray-400 hover:text-white transition"
              >
                Sign out
              </button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login" className={linkBase}>Sign in</Link>
              <Link
                to="/register"
                data-testid="nav-register"
                className={`text-sm font-semibold px-4 py-2 rounded-full ${brand === "ai" ? "btn-mint" : "btn-purple"}`}
              >
                Get access
              </Link>
            </>
          )}
        </div>

        <button
          onClick={() => setOpen(!open)}
          className="md:hidden p-2 text-white"
          data-testid="nav-mobile-toggle"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t hairline glass">
          <div className="px-6 py-4 flex flex-col gap-3">
            {items.map((it) => (
              <Link key={it.to} to={it.to} onClick={() => setOpen(false)} className={linkBase}>
                {it.label}
              </Link>
            ))}
            <button
              onClick={() => { navigate(brand === "ai" ? "/studio" : "/academy"); setOpen(false); }}
              className="text-left text-xs tracking-[0.25em] uppercase text-gray-400"
            >
              Switch to {brand === "ai" ? "Studio" : "Academy"}
            </button>
            {user ? (
              <Link to="/dashboard" onClick={() => setOpen(false)} className={linkBase}>Dashboard</Link>
            ) : (
              <>
                <Link to="/login" onClick={() => setOpen(false)} className={linkBase}>Sign in</Link>
                <Link to="/register" onClick={() => setOpen(false)} className={linkBase}>Get access</Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
